import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { AppDispatch, RootState } from "../app/store";
import { Item } from '../utils/boxTypes';
import { getBalance } from 'keycloakauthmodule';

// Define the initial state
interface WinningItemsState {
  winningItems: Item[];
  rejectWinningItemStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  rejectWinningItemError: string | null;
  isRejectWinningItemInprogress: boolean;
}

const initialState: WinningItemsState = {
  winningItems: [],
  rejectWinningItemStatus: 'idle',
  rejectWinningItemError: null,
  isRejectWinningItemInprogress: false,
};

// Async thunk for rejecting a winning item
export const rejectWinningItem = createAsyncThunk<
  { id: string; detail: string }, // Return type on success
  {id:string, itemId:string, type:string}, // Argument type (item ID)
  { rejectValue: string; dispatch: AppDispatch; state: RootState }
>(
  'winningItems/rejectWinningItem',
  async ({id, itemId, type}, { rejectWithValue , dispatch, getState }) => {
    try {
      const response = await axios.post(`item/reject?id=${id}&itemType=${type}`);
              await dispatch(getBalance()).unwrap();
      
      return { id, detail: response.data.detail }; // Return the item ID and success message
    } catch (error: any) {
      const errorDetail = error.response?.data?.detail || error.message || 'Unexpected error';
      return rejectWithValue(errorDetail);
    }
  }
);

const winningSlice = createSlice({
  name: 'winningItems',
  initialState,
  reducers: {
    setWinningItems: (state, action: PayloadAction<Item[]>) => {
      state.winningItems = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(rejectWinningItem.pending, (state) => {
        state.rejectWinningItemStatus = 'loading';
        state.rejectWinningItemError = null;
        state.isRejectWinningItemInprogress = true;
      })
      .addCase(rejectWinningItem.fulfilled, (state, action: PayloadAction<{ id: string; detail: string }>) => {
        state.rejectWinningItemStatus = 'succeeded';
        state.isRejectWinningItemInprogress = false;
        state.rejectWinningItemError = null;

        // Update the isRejected flag for the rejected item
        const rejectedItemIndex = state.winningItems.findIndex(item => item.id === action.payload.id);
        if (rejectedItemIndex !== -1) {
          state.winningItems[rejectedItemIndex].isRejected = true;
        }
      })
      .addCase(rejectWinningItem.rejected, (state, action: PayloadAction<string | undefined>) => {
        state.rejectWinningItemStatus = 'failed';
        state.rejectWinningItemError = action.payload || 'Failed to reject winning item';
        state.isRejectWinningItemInprogress = false;
      });
  },
});


export const { setWinningItems } = winningSlice.actions;

export default winningSlice.reducer;
